import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { getPaddle } from "@/lib/paddle";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Loader2, CreditCard, AlertTriangle, XCircle } from "lucide-react";

function subscribeButtonLabel({ isCurrent, isBusy, isConfigured }) {
  if (isCurrent) return "Current plan";
  if (isBusy) return <><Loader2 className="h-4 w-4 animate-spin"/> Opening checkout…</>;
  if (!isConfigured) return "Not configured";
  return <><CreditCard className="h-4 w-4"/> Subscribe</>;
}

export default function Billing() {
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [busyTier, setBusyTier] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [paddleReady, setPaddleReady] = useState(false);

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get("/api/billing/plans").then((r) => r.data),
      api.get("/api/billing/subscription").then((r) => r.data).catch(() => null),
    ]);
    setPlans(p);
    setSub(s);
  };

  useEffect(() => {
    load();
    getPaddle().then((paddle) => setPaddleReady(!!paddle));
  }, []);

  const subscribe = async (tier) => {
    setBusyTier(tier);
    try {
      const paddle = await getPaddle();
      if (!paddle) throw new Error("Paddle client token missing — set REACT_APP_PADDLE_CLIENT_TOKEN in frontend/.env");

      const { data } = await api.post("/api/billing/checkout/prepare", { tier });

      paddle.Checkout.open({
        items: [{ priceId: data.price_id, quantity: 1 }],
        customer: { email: data.customer_email },
        customData: data.passthrough,
        settings: {
          displayMode: "overlay",
          theme: "dark",
          successUrl: `${window.location.origin}/app/billing?paddle=success`,
        },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Checkout failed");
    } finally {
      setBusyTier(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription at the end of the current cycle?")) return;
    setCancelBusy(true);
    try {
      await api.post("/api/billing/cancel");
      toast.success("Subscription set to cancel at cycle end");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally { setCancelBusy(false); }
  };

  // After a Paddle checkout returns success, poll for the webhook to arrive
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("paddle") !== "success") return;
    url.searchParams.delete("paddle");
    window.history.replaceState({}, "", url.toString());
    toast.success("Payment accepted — activating…");
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      const { data } = await api.get("/api/billing/subscription").catch(() => ({ data: null }));
      if (data && ["active", "trialing"].includes(data.status)) {
        setSub(data);
        clearInterval(iv);
        toast.success("Subscription active");
      } else if (tries > 20) {
        clearInterval(iv);
      }
    }, 3000);
    return () => clearInterval(iv);
  }, []);

  const anyConfigured = plans.some((p) => p.configured);

  return (
    <div className="min-h-screen">
      <div className="border-b border-border">
        <div className="px-8 py-8">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Billing · Paddle Subscriptions</div>
          <h1 className="mt-1 font-display font-black text-4xl tracking-tighter">Marketing Team: <span className="line-through text-muted-foreground">$11,500/mo</span>. ContentOS: <span className="text-primary">$79/mo.</span></h1>
        </div>
      </div>

      {!anyConfigured && (
        <div className="px-8 pt-6">
          <div className="border border-destructive/40 bg-destructive/5 rounded p-4 flex items-start gap-3" data-testid="billing-not-configured">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5"/>
            <div>
              <div className="font-semibold">Paddle is not configured yet.</div>
              <div className="text-sm text-muted-foreground mt-1">
                Add <span className="font-mono">PADDLE_API_KEY</span>, <span className="font-mono">PADDLE_CLIENT_TOKEN</span>, <span className="font-mono">PADDLE_NOTIFICATION_KEY</span> and <span className="font-mono">PADDLE_PRICE_*</span> values to <span className="font-mono">backend/.env</span> + the <span className="font-mono">REACT_APP_PADDLE_*</span> mirrors to <span className="font-mono">frontend/.env</span>, then restart.
              </div>
            </div>
          </div>
        </div>
      )}

      {sub && (
        <div className="px-8 pt-6">
          <div className="border border-border rounded p-6 bg-card flex items-start justify-between gap-4 flex-wrap" data-testid="current-sub">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Current subscription</div>
              <div className="mt-2 flex items-center gap-3">
                <div className="font-display font-black text-2xl tracking-tight capitalize">{sub.tier}</div>
                <span className={`pillar-badge ${sub.status === "active" ? "text-primary border-primary/40" : ""}`}>{sub.status}</span>
                {sub.cancel_at_cycle_end && <span className="pillar-badge text-destructive border-destructive/30">Cancels at cycle end</span>}
              </div>
              {sub.current_period_end && (
                <div className="mt-2 text-xs font-mono text-muted-foreground">
                  Next billing: {new Date(sub.current_period_end).toLocaleDateString()}
                </div>
              )}
            </div>
            {!sub.cancel_at_cycle_end && (
              <Button variant="outline" onClick={cancel} disabled={cancelBusy} data-testid="btn-cancel-sub">
                {cancelBusy ? <><Loader2 className="h-4 w-4 animate-spin"/> Cancelling…</> : <><XCircle className="h-4 w-4"/> Cancel</>}
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="p-8 grid md:grid-cols-3 gap-4">
        {plans.map((t) => (
          <div
            key={t.tier}
            data-testid={`plan-${t.tier}`}
            className={`p-8 rounded border ${t.popular ? "border-primary bg-background" : "border-border bg-background"} relative`}
          >
            {t.popular && (
              <div className="absolute -top-3 left-6 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest px-3 py-1 rounded">Most popular</div>
            )}
            <div className="font-display font-bold text-2xl tracking-tight">{t.name}</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-display font-black text-5xl">${t.price_usd}</span>
              <span className="text-muted-foreground">/mo</span>
            </div>
            <ul className="mt-6 space-y-3">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-primary mt-0.5 shrink-0"/>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              data-testid={`btn-subscribe-${t.tier}`}
              onClick={() => subscribe(t.tier)}
              disabled={!t.configured || !paddleReady || busyTier === t.tier || (sub && sub.tier === t.tier && sub.status === "active")}
              className="mt-8 w-full h-11"
              variant={t.popular ? "default" : "outline"}
            >
              {subscribeButtonLabel({
                isCurrent: !!(sub && sub.tier === t.tier && sub.status === "active"),
                isBusy: busyTier === t.tier,
                isConfigured: t.configured,
              })}
            </Button>
          </div>
        ))}
      </div>

      <div className="px-8 pb-10">
        <div className="border border-border rounded p-6 bg-secondary/40 max-w-3xl">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">How billing works</div>
          <div className="mt-2 text-sm leading-relaxed">
            Subscriptions are handled by Paddle in USD. Paddle acts as the Merchant of Record — they handle tax, fraud, and payment methods worldwide. On checkout you authorize the recurring charge; Paddle sends webhooks that update your plan status here. Cancel any time — access remains until the current cycle ends.
          </div>
        </div>
      </div>
    </div>
  );
}
